import React, { useEffect, useState, useRef } from 'react';
import RibbonNav from "../RibbonNav";
import DashboardCardComponent from "./DashboardCardComponent";
import DashboardGardenGrid from "./DashboardGardenGrid";
import DashboardGardenSnapshotComponent from "./DashboardGardenSnapshotComponent";
import DashboardJournalComponent from "./DashboardJournalComponent";
import DashboardPlantStatusComponent from "./DashboardPlantStatusComponent";
import DashboardRemindersComponent from "./DashboardRemindersComponent";
import DashboardGardeningTipsComponent from "./DashboardGardeningTipsComponent";
import DashboardWeatherHelperComponent from "./DashboardWeatherHelperComponent";
import LeafLoader from "../reusableComponents/LeafLoader";
import "../../css/dashboardStylings/DashboardStyling.css";
import {useNavigate} from "react-router-dom";

const Dashboard = () => {
    const [gardens, setGardens] = useState([]);
    const [justAllPlants, setJustAllPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCards, setVisibleCards] = useState(new Set());
    const navigate = useNavigate();
    const cardRefs = useRef([]);

    useEffect(() => {
        // fetch all plants at once
        const fetchGardensWithPlantsOptimized = async () => {
            try {
                setLoading(true);

                // Fetch gardens and all plants in parallel
                const [gardensResponse, plantsResponse] = await Promise.all([
                    fetch('/api/gardens/fetchGardens'),
                    fetch('/api/gardens/fetchDashboardPlants')
                ]);

                const gardensResult = await gardensResponse.json();
                const plantsResult = await plantsResponse.json();

                if (!gardensResponse.ok) {
                    console.error('Failed to load gardens:', gardensResult.error);
                    fallbackGarden();
                    return;
                }

                // Group plants by garden_id
                const plantsByGarden = {};
                if (plantsResponse.ok && plantsResult.data) {
                    setJustAllPlants(plantsResult.data);
                    plantsResult.data.forEach(plant => {
                        if (!plantsByGarden[plant.garden_id]) {
                            plantsByGarden[plant.garden_id] = [];
                        }
                        plantsByGarden[plant.garden_id].push(plant);
                    });
                }

                // Combine gardens with their plants
                const gardensWithPlants = gardensResult.data.map(garden => ({
                    ...garden,
                    plants: plantsByGarden[garden.id] || []
                }));

                setGardens(gardensWithPlants);

            } catch (error) {
                console.error('Fetch error:', error);
                fallbackGarden();
            } finally {
                setLoading(false);
            }
        };

        // just for testing purposes
        const fallbackGarden = () => {
            setGardens([
                {
                    id: 0,
                    name: "Example Garden",
                    matrix: "0,0,1,0,0\n0,1,1,1,0\n0,1,1,1,0\n0,1,1,1,0\n0,0,1,0,0\n",
                    width: 3,
                    height: 3,
                    plants: []
                }
            ]);
            setLoading(false);
        };

        // fetch all the gardens + plants
        fetchGardensWithPlantsOptimized();

    }, []);

    // Intersection Observer for scroll animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cardIndex = parseInt(entry.target.dataset.cardIndex);
                        setVisibleCards(prev => new Set([...prev, cardIndex]));
                    }
                });
            },
            {
                threshold: 0.01,
                rootMargin: '50px 0px -50px 0px'
            }
        );

        cardRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            cardRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, [loading]);

    const dashboardCards = [
        {
            title: "🌱 Your Gardens",
            content: gardens.length > 0 ? (
                <div className={`garden-row-card-body`}>
                    <div className="scrollable-garden-row">
                        {gardens.slice(0, 5).map((garden, index) => (
                            <div
                                key={garden.id || index}
                                className="individual-garden"
                            >
                                <DashboardGardenGrid
                                    gardenMatrix={garden.matrix}
                                    plants={garden.plants}
                                    gardenManager={false}
                                />
                                <h4>{garden.name || 'Unnamed Garden'}</h4>
                                <p className="plant-count">
                                    {garden.plants?.length || 0} plants
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className={`click-to-view-all-button`} onClick={() => navigate("/garden-manager")}>
                        <div className={`click-to-view-all-text`}>
                            View and edit your gardens
                        </div>
                    </div>
                </div>
            ) : (
                <div className={`empty-entries-body`}>
                    <div className="no-entries-message">
                        <div>You have not created any gardens yet, jump on it!</div>
                        <button className="start-entry-button" onClick={() => navigate("/in-ground-layout-page")}>
                            Create Garden
                        </button>
                    </div>
                </div>
            )
        },
        {
            title: "📸 Garden Snapshot",
            content: <DashboardGardenSnapshotComponent gardenMap={gardens} />
        },
        {
            title: "🌿 Plant Status",
            content: <DashboardPlantStatusComponent allUserPlants={justAllPlants} onPlantStatusClick={() => navigate("/garden-manager")}/>
        },
        {
            title: "📓 Garden Journal",
            content: <DashboardJournalComponent />
        },
        {
            title: "⏰ Garden Reminders",
            content: <DashboardRemindersComponent allUserPlants={justAllPlants} gardens={gardens} />
        },
        {
            title: "🧑‍🌾 Gardening Tips",
            content: <DashboardGardeningTipsComponent />
        },
        {
            title: "⛅ Weather Helper",
            content: <DashboardWeatherHelperComponent />
        }
    ];

    if (loading) {
        return (
            <div className="dashboard">
                <RibbonNav pageName={"Dashboard"}/>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <LeafLoader
                        count={7}
                        size={20}
                        speed="fast"
                        radius={30}
                    />
                    <p>Loading gardens...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <RibbonNav pageName={"Dashboard"}/>

            {dashboardCards.map((card, index) => (
                <div
                    key={index}
                    ref={(el) => cardRefs.current[index] = el}
                    data-card-index={index}
                    className={`dashboard-garden-card ${
                        visibleCards.has(index) ? 'card-visible' : 'card-hidden'
                    } ${index % 2 === 0 ? 'slide-from-left' : 'slide-from-right'}`}
                >
                    <DashboardCardComponent
                        title={card.title}
                        variant="primary"
                        gradient={true}
                    >
                        {card.content}
                    </DashboardCardComponent>
                </div>
            ))}
        </div>
    );
};

export default Dashboard;